import React, { KeyboardEvent, forwardRef } from 'react';

import {
  Input,
  InputGroup,
  InputRightElement,
  Box,
  Flex,
} from '@chakra-ui/react';

import { CloseCircleIcon, MagnifyingGlass } from '../../../Icons';
import { EColor } from '../../../Theme';

type NewSearchInputProps = {
  autoFocus?: boolean;
  isCollapsed?: boolean;
  placeholder?: string;
  value?: string;
  withSearchIcon?: boolean;
  hasCloseIcon?: boolean;
  showClearInputButton?: boolean;
  onChange?: (value: string) => void;
  onEnter?: (value?: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onEscape?: () => void;
  onClick?: () => void;
  onClear?: () => void;
};

const NewSearchInput = forwardRef<HTMLInputElement, NewSearchInputProps>(
  function NewSearchInput(
    {
      autoFocus = false,
      isCollapsed = false,
      hasCloseIcon = false,
      placeholder = 'Search',
      value,
      withSearchIcon = true,
      showClearInputButton = false,
      onChange,
      onEnter,
      onFocus,
      onBlur,
      onEscape,
      onClick,
      onClear,
    },
    ref
  ) {
    const onKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          onEnter?.(value);
          break;
        case 'Escape':
          event.preventDefault();
          onEscape?.();
          break;
        default:
          break;
      }
    };

    return (
      <Box
        h="40px"
        position="relative"
        transition="all 0.3s ease-in-out"
        w="100%"
      >
        <InputGroup size="md" w="100%">
          {/* Search Icon - visual only, not clickable */}
          {withSearchIcon && (
            <Flex
              alignItems="center"
              bottom="0"
              justifyContent="center"
              left="12px"
              pointerEvents="none"
              position="absolute"
              top="0"
              zIndex={1}
            >
              <MagnifyingGlass
                color={EColor.Black}
                height="20px"
                opacity={isCollapsed ? 0.64 : 1}
                width="20px"
              />
            </Flex>
          )}

          <Input
            _focus={{
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              borderColor: EColor.Black,
            }}
            _focusVisible={{
              outline: 'none',
            }}
            _placeholder={{
              color: 'rgba(0, 0, 0, 0.48)',
              fontSize: '14px',
            }}
            autoFocus={autoFocus}
            backgroundColor="rgba(0, 0, 0, 0.05)"
            borderColor="transparent"
            borderRadius="full"
            borderWidth="2px"
            color={EColor.Black}
            fontWeight="normal"
            h="40px"
            pl="35px"
            placeholder={placeholder}
            pr={
              showClearInputButton || (hasCloseIcon && !isCollapsed)
                ? '40px'
                : undefined
            }
            ref={ref}
            transition="all 0.3s"
            value={value}
            w="100%"
            onBlur={onBlur}
            onChange={(e) => onChange?.(e.target.value)}
            onClick={onClick}
            onFocus={onFocus}
            onKeyUp={onKeyUp}
          />

          {/* Clear button */}
          {showClearInputButton && (
            <InputRightElement h="40px" w="40px">
              <Box
                as="button"
                cursor="pointer"
                display="flex"
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  onClear?.();
                }}
                onMouseDown={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                }}
              >
                <CloseCircleIcon boxSize="16px" color={EColor.Black} />
              </Box>
            </InputRightElement>
          )}
        </InputGroup>
      </Box>
    );
  }
);

NewSearchInput.displayName = 'NewSearchInput';

export { NewSearchInput };
